import { Box, Flex, Text, HStack, VStack } from '@chakra-ui/layout';
import { LinkBox, LinkOverlay } from '@chakra-ui/react';
import { getUserFullName } from '@duke-hq/lib';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { Prisma } from '@duke-hq/svc-api-db';
import { Link as RouterLink } from 'react-router-dom';
import { CopyToClipboardPopover } from '@duke-hq/ui/components/CopyToClipboardPopover/CopyToClipboardPopover';

export interface StudentDetailsLabels {
  learner: string;
  student: string;
  viewProfile: string;
}

const defaultLabels: StudentDetailsLabels = {
  learner: 'Learner',
  student: 'Student',
  viewProfile: 'View profile',
};

function StudentDetails({
  user,
  learner,
  labels,
}: {
  user: Prisma.StudentUserGetPayload<{
    select: {
      id: true;
      first_name: true;
      last_name: true;
      picture_url: true;
      email: true;
      secondaryEmail: true;
    };
  }>;
  learner?: {
    id: string;
    first_name: string;
    last_name: string | null;
    email: string | null;
    phone_number: string | null;
  } | null;
  labels?: Partial<StudentDetailsLabels>;
}): React.ReactElement {
  const l = { ...defaultLabels, ...labels };
  const profilePath = learner ? `/team/members/${learner.id}` : `/student/${user.id}/details`;

  return (
    <Box
      as={LinkBox}
      gap="5px"
      alignItems="center"
      backgroundColor="bg.surface"
      rounded={6}
      borderWidth={1}
      borderColor="border.default"
      p={3}
      minW="40%"
      transition="border-color 0.15s, box-shadow 0.15s"
      _hover={{ borderColor: 'brand.500', boxShadow: 'sm' }}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="medium">{learner ? l.learner : l.student}</Text>
        <LinkOverlay
          as={RouterLink}
          to={profilePath}
          fontWeight="semibold"
          fontSize="xs"
          backgroundColor="bg.muted"
          rounded="md"
          px={2}
          py={1}
        >
          {l.viewProfile}
        </LinkOverlay>
      </Flex>

      <Flex mt="9px" gap={3} alignItems="center">
        <UserAvatar
          user={{
            first_name: user.first_name,
            last_name: user.last_name,
            picture_url: user.picture_url,
          }}
          size="sm"
        />
        <VStack justifyContent="start" alignItems="start" gap={0}>
          <Text fontSize="sm" fontWeight="medium">
            {getUserFullName({
              first_name: user.first_name,
              last_name: user.last_name,
              picture_url: user.picture_url,
            })}
          </Text>

          <HStack spacing="1">
            <Text fontSize="sm" color="bg.inverse-emphasis">
              {user.email}
            </Text>
            {user.secondaryEmail != null ? (
              <Box position="relative" zIndex={1}>
                <CopyToClipboardPopover value={user.secondaryEmail} />
              </Box>
            ) : null}
          </HStack>
        </VStack>
      </Flex>
    </Box>
  );
}

export default StudentDetails;
